import { formatMoney, QUOTE_STATUS_LABELS, type QuoteStatus } from '@mt/shared';
import {
  ChevronLeft,
  ClipboardList,
  FileCheck2,
  FileText,
  Inbox,
  Loader2,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  Paperclip,
  PhoneCall,
  Play,
  Plus,
  QrCode,
  ShoppingBag,
  Search,
  Send,
  UserRound,
  X,
  Zap,
} from 'lucide-react';
import * as DM from '@radix-ui/react-dropdown-menu';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/states';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useHotkeys } from '@/lib/hotkeys';
import { cn } from '@/lib/utils';
import {
  CHANNEL_LABELS,
  SHARED_CHANNELS,
  useCallCustomer,
  useMarkRead,
  usePhoneSettings,
  useSendableQuotes,
  useSendFile,
  useSendText,
  useSetStatus,
  useDeletePrivateConversation,
  useZaloAccounts,
  useZaloConversation,
  useZaloConversations,
  useZaloMessages,
  type ZaloAccount,
  type ZaloConversation,
  type ZaloMessage,
} from './api';
import { BotDraftBar } from './BotPanel';
import { EmojiPicker, ImageActionBar, insertAtCaret, useImageActions, type ImageActions } from './ChatExtras';
import { filesFromDrop, isDragAccepted, onInboxOpen, onInboxShow, readInboxImageRef, setInboxImageDrag, takeInboxDraft } from './inbox-bridge';
import { SummaryDialog } from './SummaryDialog';
import { ConversationMemoryCard } from './MemoryCard';
import { AccountsDialog, AssignDialog, ChannelBadge, initials, InternalDialog, LinkCustomerDialog, NewChatDialog, SendCertificatesDialog, SendQuoteDialog, StatusDot } from './ZaloDialogs';

/**
 * Hộp thư khách hàng (CR-014): tab ở mép phải mọi trang, bấm (hoặc Alt+Z) để trượt ra / đóng nhanh như khung AI Minh Tiến.
 *  - Mở gọn (420 px): 1 cột, danh sách ↔ khung chat.
 *  - Mở rộng: danh sách + chat + thông tin khách.
 *  - Điện thoại: mở toàn màn hình.
 * Trạng thái mở / rộng nhớ theo trình duyệt.
 */
const PREF_KEY = 'mt.inbox';
interface Prefs {
  open: boolean;
  wide: boolean;
  conversationId: number | null;
}
function loadPrefs(): Prefs {
  try {
    return { open: false, wide: false, conversationId: null, ...(JSON.parse(localStorage.getItem(PREF_KEY) ?? '{}') as Partial<Prefs>) };
  } catch {
    return { open: false, wide: false, conversationId: null };
  }
}

const fmtTime = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Hôm qua';
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
};
const dayLabel = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Hôm nay';
  return d.toLocaleDateString('vi-VN', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
};

/**
 * Kết quả đăng nhập Zalo OA / Facebook (?hop-thu=… hoặc ?hop-thu-loi=…): đọc ngay khi tải trang, trước khi
 * màn đăng nhập / router đổi địa chỉ, rồi bỏ tham số khỏi thanh địa chỉ.
 */
let oauthReturn: { ok: boolean; text: string } | null = (() => {
  if (typeof window === 'undefined') return null;
  const q = new URLSearchParams(window.location.search);
  const ok = q.get('hop-thu');
  const err = q.get('hop-thu-loi');
  if (!ok && !err) return null;
  q.delete('hop-thu');
  q.delete('hop-thu-loi');
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${q.size ? `?${q}` : ''}${window.location.hash}`);
  return ok ? { ok: true, text: ok } : { ok: false, text: err! };
})();

export function InboxDrawer() {
  const { can } = useAuth();
  const enabled = can('zalo.use');
  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);
  const [listView, setListView] = useState(!prefs.conversationId);
  const [dialog, setDialog] = useState<'accounts' | 'new' | 'summary' | null>(null);
  const [search, setSearch] = useSearchParams();
  const accounts = useZaloAccounts(enabled);
  const unread = (accounts.data ?? []).reduce((s, a) => s + a.unread, 0);

  const update = (p: Partial<Prefs>) =>
    setPrefs((cur) => {
      const next = { ...cur, ...p };
      try {
        localStorage.setItem(PREF_KEY, JSON.stringify(next));
      } catch {
        /* trình duyệt chặn lưu – bỏ qua */
      }
      return next;
    });
  const toggle = () => update({ open: !prefs.open });

  useHotkeys({ 'alt+z': toggle }, enabled);
  // Quay về từ trang đăng nhập Zalo OA / Facebook: báo kết quả, mở hộp thư, bỏ tham số khỏi địa chỉ
  useEffect(() => {
    const r = oauthReturn;
    if (!enabled || !r) return;
    oauthReturn = null;
    if (r.ok) toast.success(r.text);
    else toast.error(r.text);
    update({ open: true });
    setDialog('accounts');
  }, [enabled]);
  // Bấm thông báo "Tóm tắt công việc ngày" (?tom-tat=1): mở hộp thư + bản tóm tắt, bỏ tham số khỏi địa chỉ
  useEffect(() => {
    if (!enabled || !search.has('tom-tat')) return;
    const next = new URLSearchParams(search);
    next.delete('tom-tat');
    setSearch(next, { replace: true });
    update({ open: true });
    setDialog('summary');
  }, [enabled, search, setSearch]);
  // Bấm thông báo "Khách chat trên web PCCC" (?hoi-thoai=<id>, DEC-526): mở đúng hội thoại, bỏ tham số khỏi địa chỉ
  useEffect(() => {
    const id = Number(search.get('hoi-thoai'));
    if (!enabled || !Number.isInteger(id) || id <= 0) return;
    const next = new URLSearchParams(search);
    next.delete('hoi-thoai');
    setSearch(next, { replace: true });
    update({ open: true, conversationId: id });
    setListView(false);
  }, [enabled, search, setSearch]);
  // Nút Zalo ở Việc cần làm / màn khác: mở hội thoại ngay trong Hộp thư (anh Tiến 10/10/2026)
  useEffect(() => {
    if (!enabled) return;
    return onInboxOpen((conversationId) => {
      update({ open: true, conversationId });
      setListView(false);
    });
  }, [enabled]);
  // Màn mở đầu ứng dụng điện thoại (X-PWA): mở khung ở danh sách hội thoại
  useEffect(() => {
    if (!enabled) return;
    return onInboxShow(() => {
      update({ open: true });
      setListView(true);
    });
  }, [enabled]);
  // Esc đóng khung khi không có hộp thoại nào đang mở bên trên
  useEffect(() => {
    if (!prefs.open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('[role="dialog"][data-state="open"], [data-combobox-list]')) return;
      update({ open: false });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prefs.open]);

  if (!enabled) return null;

  const select = (id: number) => {
    update({ conversationId: id });
    setListView(false);
  };
  const live = (accounts.data ?? []).filter((a) => a.status === 'connected').length;
  const total = accounts.data?.length ?? 0;

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={prefs.open}
        aria-controls="mt-inbox"
        aria-label={unread ? `Hộp thư, ${unread} tin chưa đọc` : 'Hộp thư'}
        title="Hộp thư khách hàng (Alt+Z)"
        className={cn(
          'fixed z-30 flex flex-col items-center gap-2 rounded-l-xl py-3 text-xs font-semibold shadow-lg transition-[right,background-color] duration-200',
          'right-0 bottom-24 w-8 md:top-32 md:bottom-auto md:w-9',
          prefs.open
            ? cn('max-md:hidden border border-r-0 bg-card text-muted-foreground', prefs.wide ? 'md:right-[min(1060px,calc(100vw-2rem))]' : 'md:right-[420px]')
            : 'bg-primary text-primary-foreground hover:bg-primary-hover',
        )}
      >
        <Inbox className="size-4" />
        <span className="hidden [writing-mode:vertical-rl] rotate-180 md:block">Hộp thư</span>
        {unread > 0 && (
          <span
            className={cn(
              'flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-bold',
              prefs.open ? 'bg-error text-white' : 'bg-white text-error',
            )}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      <section
        id="mt-inbox"
        role="dialog"
        aria-label="Hộp thư khách hàng"
        aria-hidden={!prefs.open}
        className={cn(
          'fixed z-40 flex flex-col border-l bg-card shadow-2xl transition-[transform,width,visibility] duration-200 ease-out',
          'inset-0 md:top-16 md:left-auto',
          prefs.wide ? 'md:w-[min(1060px,calc(100vw-2rem))]' : 'md:w-[420px]',
          prefs.open ? 'visible translate-x-0' : 'invisible translate-x-full',
        )}
      >
        <header className="flex h-13 shrink-0 items-center gap-2 border-b px-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Inbox className="size-4" />
          </span>
          <div className="min-w-0 flex-1 leading-tight">
            <div className="text-sm font-semibold">Hộp thư</div>
            <button
              type="button"
              onClick={() => setDialog('accounts')}
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
            >
              <StatusDot status={live ? 'connected' : total ? 'needs_login' : 'disconnected'} className="size-1.5" />
              {total ? `${live}/${total} nick Zalo đang kết nối` : 'Chưa có nick Zalo · bấm để thêm'}
            </button>
          </div>
          <IconBtn label="Tin nhắn mới theo SĐT" onClick={() => setDialog('new')}>
            <Plus />
          </IconBtn>
          <IconBtn label="Tóm tắt công việc ngày" onClick={() => setDialog('summary')}>
            <ClipboardList />
          </IconBtn>
          <IconBtn label="Kênh nhắn tin · quét QR" onClick={() => setDialog('accounts')}>
            <QrCode />
          </IconBtn>
          <IconBtn label={prefs.wide ? 'Thu gọn' : 'Mở rộng'} onClick={() => update({ wide: !prefs.wide })} className="max-md:hidden">
            {prefs.wide ? <Minimize2 /> : <Maximize2 />}
          </IconBtn>
          <IconBtn label="Đóng (Esc)" onClick={() => update({ open: false })}>
            <X />
          </IconBtn>
        </header>

        {prefs.open && (
          <div className={cn('grid min-h-0 flex-1', prefs.wide ? 'md:grid-cols-[320px_minmax(0,1fr)_300px]' : 'grid-cols-1')}>
            <ConversationList
              className={cn(!prefs.wide && !listView && 'hidden', prefs.wide && !listView && 'max-md:hidden')}
              selectedId={prefs.conversationId}
              onSelect={select}
            />
            {prefs.conversationId ? (
              <ChatPane
                key={prefs.conversationId}
                id={prefs.conversationId}
                wide={prefs.wide}
                className={cn(listView && (prefs.wide ? 'max-md:hidden' : 'hidden'), prefs.wide && 'border-x')}
                onBack={() => setListView(true)}
                onSwitch={select}
                onGone={() => {
                  update({ conversationId: null });
                  setListView(true);
                }}
              />
            ) : (
              prefs.wide && (
                <div className="flex items-center justify-center border-x bg-muted/30 text-sm text-muted-foreground max-md:hidden">Chọn một hội thoại</div>
              )
            )}
            {prefs.wide && (prefs.conversationId ? <CustomerSide id={prefs.conversationId} /> : <div className="max-md:hidden" />)}
          </div>
        )}
      </section>

      <AccountsDialog open={dialog === 'accounts'} onClose={() => setDialog(null)} />
      <NewChatDialog open={dialog === 'new'} onClose={() => setDialog(null)} onOpened={(c) => select(c.id)} />
      <SummaryDialog
        open={dialog === 'summary'}
        onClose={() => setDialog(null)}
        onOpenConversation={(id) => {
          setDialog(null);
          update({ open: true });
          select(id);
        }}
      />
    </>
  );
}

function IconBtn({ label, onClick, children, className }: { label: string; onClick: () => void; children: React.ReactNode; className?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn('rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground [&_svg]:size-4', className)}
    >
      {children}
    </button>
  );
}

function Avatar({ name, url, className }: { name: string | null; url: string | null; className?: string }) {
  const [broken, setBroken] = useState(false);
  return url && !broken ? (
    <img src={url} alt="" onError={() => setBroken(true)} className={cn('size-9 shrink-0 rounded-full border object-cover', className)} />
  ) : (
    <span
      className={cn('flex size-9 shrink-0 items-center justify-center rounded-full border bg-muted text-xs font-semibold text-muted-foreground', className)}
    >
      {initials(name)}
    </span>
  );
}

// ───────────────────────── Danh sách hội thoại ─────────────────────────

/** "Khách" = chat 1-1 (mặc định), "Nhóm" = nhóm Zalo tách riêng (anh Tiến 13/10/2026 "không nên để quá nhiều nhóm"). */
type Chip = 'all' | 'groups' | 'unread' | 'mine';

/**
 * Bộ lọc nick (anh Tiến 13/10/2026): GĐ / Quản trị / TP thấy nhiều nick nên mặc định chỉ hiện "nick của tôi + kênh chung";
 * nick NV nằm nhóm "Nhân viên (giám sát)". Lựa chọn nhớ theo trình duyệt.
 */
type NickFilter = 'mine' | 'all' | number;
const NICK_KEY = 'mt.inbox.nick';
function loadNick(): NickFilter {
  try {
    const v = localStorage.getItem(NICK_KEY);
    if (v === 'all') return 'all';
    const n = Number(v);
    return v && Number.isInteger(n) && n > 0 ? n : 'mine';
  } catch {
    return 'mine';
  }
}
function saveNick(v: NickFilter) {
  try {
    localStorage.setItem(NICK_KEY, String(v));
  } catch {
    /* trình duyệt chặn lưu – bỏ qua */
  }
}

type NickGroup = 'mine' | 'shared' | 'staff';
const nickGroup = (a: ZaloAccount, userId: number | undefined): NickGroup =>
  SHARED_CHANNELS.includes(a.channel) ? 'shared' : a.ownerUserId === userId ? 'mine' : 'staff';

/** Màu riêng từng nick, cố định theo thứ tự danh sách nick (nick của mình trước). */
const NICK_COLORS = [
  'text-primary',
  'text-info',
  'text-warning',
  'text-stage-negotiating',
  'text-error',
  'text-stage-caring',
  'text-stage-quoted',
  'text-muted-foreground',
];
const nickColor = (accs: ZaloAccount[], accountId: number) => {
  const i = accs.findIndex((a) => a.id === accountId);
  return NICK_COLORS[(i < 0 ? accountId : i) % NICK_COLORS.length];
};

function NickTag({ via, accs, className }: { via: { accountId: number; accountName: string }[]; accs: ZaloAccount[]; className?: string }) {
  return (
    <span className={cn('flex min-w-0 items-center gap-1 truncate', className)} title={via.map((v) => v.accountName).join(', ')}>
      {via.length > 1 && <span className="shrink-0">qua</span>}
      {via.map((v) => (
        <span key={v.accountId} className="inline-flex min-w-0 items-center gap-0.5">
          <span className={cn('text-[9px] leading-none', nickColor(accs, v.accountId))}>●</span>
          <span className="truncate">{v.accountName}</span>
        </span>
      ))}
    </span>
  );
}

function ConversationList({ selectedId, onSelect, className }: { selectedId: number | null; onSelect: (id: number) => void; className?: string }) {
  const accounts = useZaloAccounts();
  const { user } = useAuth();
  const [nick, setNickState] = useState<NickFilter>(loadNick);
  const setNick = (v: NickFilter) => {
    setNickState(v);
    saveNick(v);
  };
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [chip, setChip] = useState<Chip>('all');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);
  const accs = useMemo(() => accounts.data ?? [], [accounts.data]);
  const many = accs.length > 1;
  // Nick đã nhớ bị gỡ / mất quyền → về "mọi nick"
  const accountId = typeof nick === 'number' && accs.some((a) => a.id === nick) ? nick : undefined;
  const nickValue = typeof nick === 'number' ? (accountId ?? (accounts.data ? 'all' : nick)) : nick;
  const list = useZaloConversations(
    {
      accountId,
      scope: many && nickValue === 'mine' ? 'mine' : undefined,
      q: debounced,
      // Đang tìm thì "Khách" tìm cả nhóm, để gõ tên nhóm vẫn ra
      threadType: chip === 'groups' ? 'group' : chip === 'all' && !debounced.trim() ? 'user' : undefined,
      unread: chip === 'unread',
      mine: chip === 'mine',
    },
    !accounts.isPending,
  );
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const groups = useMemo(() => {
    const g: Record<NickGroup, ZaloAccount[]> = { mine: [], shared: [], staff: [] };
    for (const a of accs) g[nickGroup(a, user?.id)].push(a);
    return g;
  }, [accs, user?.id]);
  // Nick hiện trên từng dòng chỉ khi người xem có nhiều nick và không lọc riêng 1 nick
  const showNick = many && !accountId;

  return (
    <div className={cn('flex min-h-0 min-w-0 flex-col', className)}>
      <div className="space-y-2 border-b p-2.5">
        <label className="flex h-9 items-center gap-2 rounded-lg bg-muted px-2.5 text-sm text-muted-foreground">
          <Search className="size-4 shrink-0" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Tìm tên, SĐT, mã khách, nội dung"
            className="min-w-0 flex-1 bg-transparent text-foreground outline-none placeholder:text-muted-foreground"
          />
        </label>
        <div className="flex flex-wrap items-center gap-1.5">
          {(
            [
              ['all', 'Khách'],
              ['groups', 'Nhóm'],
              ['unread', 'Chưa đọc'],
              ['mine', 'Của tôi'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setChip(k)}
              className={cn(
                'h-7 rounded-full border px-2.5 text-xs',
                chip === k ? 'border-foreground font-medium text-foreground' : 'text-muted-foreground hover:bg-muted',
              )}
            >
              {label}
            </button>
          ))}
          {many && (
            <select
              value={String(nickValue)}
              onChange={(e) => setNick(e.target.value === 'mine' || e.target.value === 'all' ? e.target.value : Number(e.target.value))}
              className="ml-auto h-7 max-w-44 rounded-full border bg-card px-2 text-xs text-muted-foreground"
              aria-label="Lọc theo nick"
            >
              <option value="mine">Nick của tôi + kênh chung</option>
              <option value="all">Mọi nick</option>
              {(
                [
                  ['mine', 'Của tôi'],
                  ['shared', 'Kênh chung công ty'],
                  ['staff', 'Nhân viên (giám sát)'],
                ] as const
              ).map(
                ([k, label]) =>
                  groups[k].length > 0 && (
                    <optgroup key={k} label={label}>
                      {groups[k].map((a) => (
                        <option key={a.id} value={a.id}>
                          {k === 'staff' ? `${a.name} · ${a.ownerName}` : a.name}
                        </option>
                      ))}
                    </optgroup>
                  ),
              )}
            </select>
          )}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {list.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            {debounced || chip !== 'all' ? 'Không có hội thoại phù hợp' : 'Chưa có hội thoại. Tin nhắn Zalo mới sẽ hiện ở đây.'}
          </p>
        ) : (
          items.map((c) => (
            <ConversationRow
              key={c.id}
              c={c}
              accs={showNick ? accs : null}
              active={c.id === selectedId || !!c.via?.some((v) => v.id === selectedId)}
              onClick={() => onSelect(c.id)}
            />
          ))
        )}
        {list.hasNextPage && (
          <button
            type="button"
            onClick={() => void list.fetchNextPage()}
            className="w-full py-3 text-xs text-primary hover:underline"
            disabled={list.isFetchingNextPage}
          >
            {list.isFetchingNextPage ? 'Đang tải…' : 'Xem thêm'}
          </button>
        )}
      </div>
    </div>
  );
}

function ConversationRow({ c, accs, active, onClick }: { c: ZaloConversation; accs: ZaloAccount[] | null; active: boolean; onClick: () => void }) {
  const name = c.contactName ?? c.phone ?? 'Không tên';
  const via = c.via?.length ? c.via : [{ accountId: c.accountId, accountName: c.accountName }];
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('grid w-full grid-cols-[36px_minmax(0,1fr)_auto] gap-2.5 border-b px-3 py-2.5 text-left hover:bg-muted/50', active && 'bg-muted')}
    >
      <Avatar name={name} url={c.avatarUrl} />
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 truncate text-sm font-semibold">
          <ChannelBadge channel={c.channel} />
          <span className="truncate">{name}</span>
          {c.threadType === 'group' && <span className="font-normal text-muted-foreground"> · nhóm</span>}
        </span>
        <span className={cn('block truncate text-xs', c.unreadCount ? 'text-foreground' : 'text-muted-foreground')}>
          {c.lastDirection === 'out' && 'Bạn: '}
          {c.lastMessageText ?? ''}
        </span>
        <span className="mt-0.5 flex gap-2 text-[11px]">
          {c.customerCode ? (
            <span className="font-medium text-primary">{c.customerCode}</span>
          ) : (
            c.threadType === 'user' && <span className="text-warning">Chưa gắn khách</span>
          )}
          {c.assignedName && <span className="truncate text-muted-foreground">→ {c.assignedName}</span>}
          {c.status === 'closed' && <span className="text-muted-foreground">Đã đóng</span>}
          {accs && <NickTag via={via} accs={accs} className="ml-auto text-muted-foreground" />}
        </span>
      </span>
      <span className="flex flex-col items-end gap-1 text-[11px] text-muted-foreground tabular-nums">
        {fmtTime(c.lastMessageAt)}
        {c.unreadCount > 0 && (
          <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10.5px] font-semibold text-primary-foreground">
            {c.unreadCount}
          </span>
        )}
      </span>
    </button>
  );
}

// ───────────────────────── Khung chat ─────────────────────────

function ChatPane({
  id,
  wide,
  className,
  onBack,
  onSwitch,
  onGone,
}: {
  id: number;
  wide: boolean;
  className?: string;
  onBack: () => void;
  onSwitch: (id: number) => void;
  onGone: () => void;
}) {
  const conv = useZaloConversation(id);
  const msgs = useZaloMessages(id);
  const markRead = useMarkRead();
  const sendText = useSendText();
  const sendFile = useSendFile();
  const setStatus = useSetStatus();
  const removePrivate = useDeletePrivateConversation();
  const phone = usePhoneSettings();
  const callOut = useCallCustomer();
  const [text, setText] = useState('');
  const [dialog, setDialog] = useState<'quote' | 'cert' | 'customer' | 'assign' | 'internal' | 'memory' | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const stick = useRef(true);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [dropOver, setDropOver] = useState<null | 'send' | 'customer'>(null);
  const c = conv.data;
  const { can, user } = useAuth();
  const imageActions = useImageActions(c);

  // Mở từ Việc cần làm: tin soạn sẵn vào ô nhập (NV xem lại rồi gửi)
  useEffect(() => {
    const take = () => {
      const d = takeInboxDraft(id);
      if (d) setText((cur) => cur || d);
    };
    take();
    // Đang mở đúng hội thoại này rồi bấm Zalo lần nữa
    return onInboxOpen((cid) => cid === id && take());
  }, [id]);

  // Hội thoại bị xóa / mất quyền → về danh sách
  useEffect(() => {
    if (conv.isError) onGone();
  }, [conv.isError, onGone]);

  // Mở hội thoại có tin chưa đọc → đánh dấu đã đọc
  useEffect(() => {
    if (c && c.unreadCount > 0 && !markRead.isPending) markRead.mutate(c.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c?.id, c?.unreadCount]);

  const items = useMemo(() => (msgs.data ? [...msgs.data.pages].reverse().flatMap((p) => p.items) : []), [msgs.data]);
  const lastId = items[items.length - 1]?.id;

  // Tin mới: cuộn xuống cuối nếu đang ở cuối
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [lastId]);

  const send = () => {
    const t = text.trim();
    if (!t || sendText.isPending) return;
    stick.current = true;
    sendText.mutateAsync({ id, text: t }).then(
      () => setText(''),
      (e: unknown) => toast.error(e instanceof Error ? e.message : 'Không gửi được'),
    );
  };
  const upload = (file: File) => {
    if (file.size > 10 * 1024 * 1024) return toast.error('Tệp lớn hơn 10 MB');
    stick.current = true;
    sendFile.mutateAsync({ id, file }).catch((e: unknown) => toast.error(e instanceof Error ? e.message : 'Không gửi được tệp'));
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const f = [...e.clipboardData.files].find((x) => x.type.startsWith('image/'));
    if (f) {
      e.preventDefault();
      upload(f);
    }
  };

  const addEmoji = (e: string) => {
    const r = insertAtCaret(inputRef.current, text, e);
    setText(r.text);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(r.caret, r.caret);
    });
  };
  /**
   * Kéo ảnh vào khung chat (anh Tiến 10/10/2026 "kéo hình từ zalo sang"): nửa trên = gửi cho khách, nửa dưới = đọc thông tin
   * khách để gắn. Ảnh kéo từ chính hội thoại này thì chỉ đọc khách (không gửi lại).
   */
  const dropZoneOf = (e: DragEvent<HTMLDivElement>): 'send' | 'customer' => {
    const r = e.currentTarget.getBoundingClientRect();
    return e.clientY - r.top < r.height / 2 ? 'send' : 'customer';
  };
  const onDragOverChat = (e: DragEvent<HTMLDivElement>) => {
    if (!isDragAccepted(e.dataTransfer)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDropOver(dropZoneOf(e));
  };
  const onDropChat = async (e: DragEvent<HTMLDivElement>) => {
    if (!isDragAccepted(e.dataTransfer)) return;
    e.preventDefault();
    const zone = dropZoneOf(e);
    setDropOver(null);
    const ref = readInboxImageRef(e.dataTransfer);
    if (ref && ref.conversationId === id) return void imageActions.recognize?.(ref);
    let files: File[];
    try {
      files = await filesFromDrop(e.dataTransfer);
    } catch (err) {
      return void toast.error((err as Error).message);
    }
    const f = files[0];
    if (!f) return;
    if (zone === 'customer') {
      if (!f.type.startsWith('image/')) return void toast.error('Chỉ đọc được thông tin khách từ ảnh');
      return void imageActions.recognize?.(f);
    }
    if (c?.channel === 'phone') return void toast.error('Hội thoại tổng đài không gửi được ảnh');
    upload(f);
  };

  if (!c) {
    return (
      <div className={cn('flex items-center justify-center bg-muted/30', className)}>
        <Spinner />
      </div>
    );
  }
  const name = c.contactName ?? c.phone ?? 'Không tên';
  // Bước 6: gọi khách qua tổng đài (tổng đài gọi máy NV trước rồi nối khách)
  const isPhone = c.channel === 'phone';
  const canCall = !!phone.data?.connected && c.threadType === 'user' && !!(c.phone || isPhone);
  const callCustomer = () =>
    callOut.mutateAsync(id).then(
      (r) => toast.success(r.message),
      (e: unknown) => toast.error(e instanceof Error ? e.message : 'Không gọi được'),
    );

  return (
    <div className={cn('flex min-h-0 min-w-0 flex-col bg-muted/30', className)}>
      <div className="flex h-14 shrink-0 items-center gap-2 border-b bg-card px-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="Danh sách hội thoại"
          className={cn('rounded-md p-1.5 text-muted-foreground hover:bg-muted', wide && 'md:hidden')}
        >
          <ChevronLeft className="size-4" />
        </button>
        <Avatar name={name} url={c.avatarUrl} />
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-sm font-semibold">{name}</div>
          <div className="truncate text-xs text-muted-foreground">
            {c.channel !== 'zalo' && `${CHANNEL_LABELS[c.channel]}: `}
            {c.via && c.via.length > 1 ? (
              // Nhóm nhiều nick công ty cùng tham gia: chọn nick để đọc / trả lời (mỗi nick 1 bản hội thoại)
              <select
                value={c.id}
                onChange={(e) => onSwitch(Number(e.target.value))}
                aria-label="Trả lời bằng nick"
                title="Trả lời bằng nick"
                className="max-w-40 rounded border-0 bg-transparent p-0 text-xs font-medium text-foreground underline decoration-dotted underline-offset-2"
              >
                {c.via.map((v) => (
                  <option key={v.id} value={v.id}>
                    Nick: {v.accountName}
                  </option>
                ))}
              </select>
            ) : (
              c.accountName
            )}
            {c.assignedName ? ` · giao cho ${c.assignedName}` : ''}
            {c.friendStatus === 'request_sent' ? ' · đã mời kết bạn' : c.friendStatus === 'stranger' ? ' · người lạ' : ''}
          </div>
        </div>
        {canCall && (
          <IconBtn label="Gọi qua tổng đài" onClick={() => void callCustomer()}>
            {callOut.isPending ? <Loader2 className="animate-spin" /> : <PhoneCall />}
          </IconBtn>
        )}
        <DM.Root>
          <DM.Trigger className="rounded-md p-1.5 text-muted-foreground outline-none hover:bg-muted" aria-label="Thao tác">
            <MoreHorizontal className="size-4" />
          </DM.Trigger>
          <DM.Portal>
            <DM.Content align="end" sideOffset={4} className="z-50 min-w-48 rounded-lg border bg-card p-1 text-sm shadow-lg">
              <MenuItem onSelect={() => setDialog('customer')}>{c.customerId ? 'Đổi khách hàng gắn' : 'Gắn khách hàng'}</MenuItem>
              <MenuItem onSelect={() => setDialog('assign')}>Giao cho nhân viên</MenuItem>
              <MenuItem onSelect={() => setDialog('memory')}>Bộ nhớ khách</MenuItem>
              <MenuItem
                onSelect={() =>
                  setStatus.mutateAsync({ id, status: c.status === 'open' ? 'closed' : 'open' }).catch(() => toast.error('Không đổi được trạng thái'))
                }
              >
                {c.status === 'open' ? 'Đóng hội thoại' : 'Mở lại hội thoại'}
              </MenuItem>
              <MenuItem onSelect={() => setDialog('internal')}>
                {c.isInternal ? `Nội bộ / Nhân viên CTY${c.employeeName ? `: ${c.employeeName}` : ''} (sửa / bỏ)` : 'Gán Nội bộ / Nhân viên CTY (trợ lý không trả lời)'}
              </MenuItem>
              {c.isPrivate && c.ownerUserId === user?.id && (
                <MenuItem
                  onSelect={() => {
                    if (!confirm('Xóa chat riêng này khỏi ERP? Zalo trên điện thoại không bị ảnh hưởng.')) return;
                    removePrivate
                      .mutateAsync(id)
                      .then(() => {
                        toast.success('Đã xóa chat riêng khỏi ERP');
                        onGone();
                      })
                      .catch(() => toast.error('Không xóa được'));
                  }}
                >
                  Xóa chat riêng khỏi ERP
                </MenuItem>
              )}
            </DM.Content>
          </DM.Portal>
        </DM.Root>
      </div>

      {/* Mở gọn: 1 dòng khách hàng thay cột thông tin */}
      <div className={cn('flex shrink-0 items-center gap-2 border-b bg-card px-3 py-1.5 text-xs', wide && 'md:hidden')}>
        <UserRound className="size-3.5 shrink-0 text-muted-foreground" />
        {c.customerId ? (
          <Link to={`/khach-hang/${c.customerId}`} className="min-w-0 truncate">
            <span className="font-medium text-primary">{c.customerCode}</span> <span className="text-muted-foreground">{c.customerName}</span>
          </Link>
        ) : (
          <button type="button" onClick={() => setDialog('customer')} className="text-warning hover:underline">
            Chưa gắn khách hàng · bấm để gắn
          </button>
        )}
      </div>

      <div
        className="relative flex min-h-0 flex-1 flex-col"
        onDragOver={onDragOverChat}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropOver(null);
        }}
        onDrop={(e) => void onDropChat(e)}
      >
        {dropOver && (
          <div className="pointer-events-none absolute inset-0 z-10 grid grid-rows-2 gap-2 bg-muted/60 p-2">
            {(['send', 'customer'] as const).map((z) => (
              <div
                key={z}
                className={cn(
                  'flex items-center justify-center rounded-xl border-2 border-dashed bg-card/90 px-3 text-center text-sm font-medium',
                  dropOver === z ? 'border-primary text-primary' : 'border-muted-foreground/40 text-muted-foreground',
                )}
              >
                {z === 'send'
                  ? isPhone
                    ? 'Hội thoại tổng đài không gửi ảnh'
                    : 'Thả vào đây: gửi ảnh cho khách'
                  : 'Thả vào đây: đọc thông tin khách và gắn khách'}
              </div>
            ))}
          </div>
        )}
        <div
          ref={scroller}
          onScroll={(e) => {
            const el = e.currentTarget;
            stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
          }}
          className={cn('flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto', wide ? 'px-6 py-4' : 'p-3')}
        >
          {msgs.hasNextPage && (
            <button
              type="button"
              onClick={() => void msgs.fetchNextPage()}
              className="self-center text-xs text-primary hover:underline"
              disabled={msgs.isFetchingNextPage}
            >
              {msgs.isFetchingNextPage ? 'Đang tải…' : 'Xem tin cũ hơn'}
            </button>
          )}
          {msgs.isLoading && <Spinner className="self-center" />}
          {items.map((m, i) => (
            <MessageItem
              key={m.id}
              m={m}
              conversationId={id}
              actions={imageActions}
              wide={wide}
              newDay={i === 0 || new Date(items[i - 1]!.sentAt).toDateString() !== new Date(m.sentAt).toDateString()}
              group={c.threadType === 'group'}
            />
          ))}
          {!msgs.isLoading && items.length === 0 && <p className="m-auto text-xs text-muted-foreground">Chưa có tin nhắn</p>}
        </div>
      </div>

      <div className="shrink-0 space-y-2 border-t bg-card p-2.5">
        <BotDraftBar conversationId={id} />
        {isPhone ? (
          <div className="flex items-center justify-between gap-2 px-1 text-xs text-muted-foreground">
            <span>Hội thoại tổng đài chỉ gọi điện. Nhắn khách qua Zalo hoặc email.</span>
            <Button size="sm" onClick={() => void callCustomer()} disabled={!canCall || callOut.isPending}>
              <PhoneCall /> Gọi lại khách
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-end gap-1.5 rounded-xl border bg-card px-2 py-1.5 focus-within:border-primary/60">
              <textarea
                ref={inputRef}
                rows={1}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKey}
                onPaste={onPaste}
                placeholder="Nhập tin nhắn…"
                title="Enter gửi · Shift+Enter xuống dòng · dán ảnh (Ctrl+V) để gửi ảnh"
                className="max-h-32 min-h-8 flex-1 resize-none bg-transparent py-1.5 text-sm outline-none [field-sizing:content] placeholder:text-muted-foreground"
              />
              <EmojiPicker onPick={addEmoji} />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                title="Gửi ảnh / tệp"
                aria-label="Gửi ảnh / tệp"
                className="rounded-md p-1.5 text-muted-foreground hover:bg-muted"
                disabled={sendFile.isPending}
              >
                {sendFile.isPending ? <Loader2 className="size-4 animate-spin" /> : <Paperclip className="size-4" />}
              </button>
              <Button size="icon" onClick={send} disabled={!text.trim() || sendText.isPending} aria-label="Gửi">
                {sendText.isPending ? <Spinner /> : <Send />}
              </Button>
              <input
                ref={fileRef}
                type="file"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) upload(f);
                  e.target.value = '';
                }}
              />
            </div>
            <div className="flex flex-wrap gap-1">
              <Button size="sm" variant="ghost" onClick={() => setDialog('quote')} disabled={c.threadType === 'group'}>
                <FileText /> Gửi báo giá
              </Button>
              {can('docs.cert.read') && (
                <Button size="sm" variant="ghost" onClick={() => setDialog('cert')} disabled={c.threadType === 'group'}>
                  <FileCheck2 /> Gửi CO/CQ
                </Button>
              )}
              <Button size="sm" variant="ghost" asChild>
                <Link to="/bao-gia-nhanh">
                  <Zap /> BG nhanh
                </Link>
              </Button>
              {can('purchase.manage') && (
                <Button size="sm" variant="ghost" asChild>
                  <Link to="/mua-hang-nhanh" title="Mua hàng nhanh: đọc phiếu / danh sách NCC gửi">
                    <ShoppingBag /> Mua nhanh
                  </Link>
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      {imageActions.dialog}
      <SendQuoteDialog conversation={c} open={dialog === 'quote'} onClose={() => setDialog(null)} />
      <SendCertificatesDialog conversation={c} open={dialog === 'cert'} onClose={() => setDialog(null)} />
      <LinkCustomerDialog conversation={c} open={dialog === 'customer'} onClose={() => setDialog(null)} />
      <AssignDialog conversation={c} open={dialog === 'assign'} onClose={() => setDialog(null)} />
      <InternalDialog conversation={c} open={dialog === 'internal'} onClose={() => setDialog(null)} />
      <Dialog
        open={dialog === 'memory'}
        onOpenChange={(v) => !v && setDialog(null)}
        title="Bộ nhớ khách"
        description={c.customerName ?? c.contactName ?? undefined}
        className="max-w-lg"
      >
        {dialog === 'memory' && <ConversationMemoryCard conversationId={id} />}
      </Dialog>
    </div>
  );
}

/** Ghi âm cuộc gọi tổng đài: tải qua API (cần đăng nhập) khi bấm nghe. */
function CallRecording({ url }: { url: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => () => void (src && URL.revokeObjectURL(src)), [src]);
  if (src) return <audio controls autoPlay src={src} className="mt-1.5 h-9 w-64 max-w-full" />;
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => {
        setBusy(true);
        api
          .blob(url.replace(/^\/api\/v1/, ''))
          .then((b) => setSrc(URL.createObjectURL(b)))
          .catch((e: unknown) => toast.error(e instanceof Error ? e.message : 'Không tải được ghi âm'))
          .finally(() => setBusy(false));
      }}
      className="mt-1.5 flex items-center gap-1.5 rounded-lg border bg-card px-2 py-1 text-xs text-foreground hover:bg-muted"
    >
      {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Play className="size-3.5" />} Nghe ghi âm
    </button>
  );
}

function MenuItem({ onSelect, children }: { onSelect: () => void; children: React.ReactNode }) {
  return (
    <DM.Item onSelect={onSelect} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 outline-none data-[highlighted]:bg-muted">
      {children}
    </DM.Item>
  );
}

function MessageItem({
  m,
  wide,
  newDay,
  group,
  conversationId,
  actions,
}: {
  m: ZaloMessage;
  wide: boolean;
  newDay: boolean;
  group: boolean;
  conversationId: number;
  actions: ImageActions;
}) {
  const out = m.direction === 'out';
  // Giữ vị trí gốc của ảnh trong danh sách tệp (máy chủ tải ảnh theo vị trí)
  const images = m.attachments.map((a, index) => ({ ...a, index })).filter((a) => a.type === 'image' && a.url);
  const files = m.attachments.filter((a) => a.type !== 'image' && a.type !== 'call_recording');
  const recordings = m.attachments.filter((a) => a.type === 'call_recording' && a.url);
  const time = new Date(m.sentAt).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
  const showText = m.text && !(m.messageType === 'sticker');
  return (
    <>
      {newDay && <span className="self-center text-[11px] text-muted-foreground">{dayLabel(m.sentAt)}</span>}
      <div className={cn('flex flex-col gap-0.5', out ? 'items-end self-end' : 'items-start self-start', wide ? 'max-w-[66%]' : 'max-w-[86%]')}>
        {group && !out && m.senderName && <span className="px-1 text-[11px] text-muted-foreground">{m.senderName}</span>}
        {images.map((a) => {
          const ref = { conversationId, messageId: m.id, index: a.index };
          return (
            <div key={a.index} className="group/img relative">
              <a
                href={a.url!}
                target="_blank"
                rel="noreferrer noopener"
                draggable
                onDragStart={(e) => setInboxImageDrag(e.dataTransfer, ref)}
                title="Kéo ảnh sang Báo giá nhanh / Mua hàng nhanh / ô Khách hàng"
              >
                <img src={a.thumbnail ?? a.url!} alt="Ảnh" loading="lazy" className="max-h-60 max-w-full rounded-xl border object-cover" />
              </a>
              <ImageActionBar
                actions={actions}
                src={ref}
                className="absolute right-1.5 bottom-1.5 opacity-0 transition-opacity group-hover/img:opacity-100 focus-within:opacity-100 max-md:opacity-100"
              />
            </div>
          );
        })}
        {m.messageType === 'sticker' && images.length === 0 && <span className="text-xs text-muted-foreground">[Sticker]</span>}
        {(showText || files.length > 0) && (
          <div
            className={cn(
              'rounded-2xl border px-3 py-2 text-sm break-words whitespace-pre-wrap',
              out ? 'rounded-br-md border-transparent bg-primary/10' : 'rounded-bl-md bg-card',
            )}
          >
            {showText && m.text}
            {recordings.map((a, i) => (
              <CallRecording key={i} url={a.url!} />
            ))}
            {files.map((a, i) => (
              <a
                key={i}
                href={a.url ?? undefined}
                target="_blank"
                rel="noreferrer noopener"
                className={cn('mt-1.5 flex min-w-52 items-center gap-2.5 rounded-lg border bg-card p-2 text-foreground', !a.url && 'pointer-events-none')}
              >
                <span className="flex h-9 w-7 shrink-0 items-center justify-center rounded border bg-muted text-[9px] font-semibold text-error">
                  {(a.name?.split('.').pop() ?? a.type).slice(0, 4).toUpperCase()}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-xs font-medium">{m.quoteCode ? `Báo giá ${m.quoteCode}` : (a.name ?? 'Tệp')}</span>
                  {a.size ? <span className="block text-[11px] text-muted-foreground">{Math.max(1, Math.round(a.size / 1024))} KB</span> : null}
                </span>
              </a>
            ))}
          </div>
        )}
        <span className="px-1 text-[11px] text-muted-foreground tabular-nums">
          {time}
          {out && (m.sentByName ?? m.senderName) ? ` · ${m.sentByName ?? m.senderName}` : ''}
          {m.quoteCode ? ' · đã ghi Gửi khách' : ''}
        </span>
      </div>
    </>
  );
}

// ───────────────────────── Cột thông tin khách (mở rộng) ─────────────────────────

function CustomerSide({ id }: { id: number }) {
  const conv = useZaloConversation(id);
  const c = conv.data;
  const quotes = useSendableQuotes(id, !!c?.customerId);
  const [dialog, setDialog] = useState<'customer' | null>(null);
  if (!c) return <div className="max-md:hidden" />;
  return (
    <aside className="min-h-0 space-y-6 overflow-y-auto p-4 text-sm max-md:hidden">
      <div>
        <h3 className="mb-2 flex items-center justify-between text-[11px] font-medium tracking-wider text-muted-foreground uppercase">
          Khách hàng
          <button type="button" onClick={() => setDialog('customer')} className="text-xs font-normal tracking-normal normal-case text-primary hover:underline">
            {c.customerId ? 'Đổi' : 'Gắn khách'}
          </button>
        </h3>
        {c.customerId ? (
          <Link to={`/khach-hang/${c.customerId}`} className="block hover:underline">
            <div className="font-semibold">{c.customerName}</div>
            <div className="text-xs text-muted-foreground">
              {c.customerCode} · {c.linkSource === 'manual' ? 'gắn tay' : 'tự khớp theo SĐT'}
            </div>
          </Link>
        ) : (
          <p className="text-xs text-warning">Chưa gắn khách hàng ERP</p>
        )}
        <dl className="mt-3 grid grid-cols-[84px_1fr] gap-x-2 gap-y-1.5 text-xs">
          <dt className="text-muted-foreground">Zalo</dt>
          <dd className="truncate">{c.contactName ?? '—'}</dd>
          <dt className="text-muted-foreground">Điện thoại</dt>
          <dd className="tabular-nums">{c.phone ?? '—'}</dd>
          <dt className="text-muted-foreground">Nick</dt>
          <dd className="truncate">{c.accountName}</dd>
          <dt className="text-muted-foreground">Phụ trách</dt>
          <dd>{c.assignedName ?? '—'}</dd>
        </dl>
        {c.isInternal && <p className="mt-2 text-xs text-muted-foreground">Nội bộ công ty: trợ lý tự động không trả lời</p>}
      </div>
      <ConversationMemoryCard conversationId={id} compact />
      {c.customerId && (
        <div>
          <h3 className="mb-2 text-[11px] font-medium tracking-wider text-muted-foreground uppercase">Báo giá gửi được</h3>
          {quotes.isLoading ? (
            <Spinner />
          ) : (quotes.data?.items.length ?? 0) === 0 ? (
            <p className="text-xs text-muted-foreground">Chưa có báo giá ERP đang mở</p>
          ) : (
            <ul className="divide-y">
              {quotes.data!.items.map((q) => (
                <li key={q.id}>
                  <Link to={`/bao-gia/${q.id}`} className="flex justify-between gap-2 py-2 text-xs hover:underline">
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{q.code}</span>
                      <span className="text-muted-foreground">{QUOTE_STATUS_LABELS[q.status as QuoteStatus] ?? q.status}</span>
                    </span>
                    <span className="tabular-nums">{formatMoney(q.grandTotal)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <LinkCustomerDialog conversation={c} open={dialog === 'customer'} onClose={() => setDialog(null)} />
    </aside>
  );
}
